import { useEffect, useState } from 'react';
import { WifiOff } from 'lucide-react';
import { retryWebSocket } from '@/hooks/useWebSocket';

/**
 * Non-blocking amber banner shown when the WebSocket connection has exhausted its retries.
 * Listens for the `ws-offline` / `ws-online` window events dispatched by useWebSocket and
 * offers a manual Retry. Renders nothing while the connection is healthy.
 */
export function WebSocketOfflineBanner(): JSX.Element | null {
  const [offline, setOffline] = useState(false);

  useEffect(() => {
    const onOffline = (): void => setOffline(true);
    const onOnline = (): void => setOffline(false);
    window.addEventListener('ws-offline', onOffline);
    window.addEventListener('ws-online', onOnline);
    return () => {
      window.removeEventListener('ws-offline', onOffline);
      window.removeEventListener('ws-online', onOnline);
    };
  }, []);

  if (!offline) return null;

  return (
    <div className="flex items-center justify-between gap-3 rounded-md border border-[#c98a00]/30 bg-[#c98a00]/10 px-4 py-2.5 text-sm">
      <div className="flex items-center gap-2 text-[#c98a00]">
        <WifiOff className="h-4 w-4 shrink-0" />
        <span>Real-time updates unavailable — refresh manually or</span>
      </div>
      <button
        onClick={() => retryWebSocket?.()}
        className="shrink-0 rounded border border-[#c98a00]/50 px-2.5 py-1 text-xs font-medium text-[#c98a00] transition-colors hover:bg-[#c98a00]/20"
      >
        Retry
      </button>
    </div>
  );
}
